import React, { useState, useEffect, useCallback } from "react";
import { useStore } from "../../../Context/context.jsx";
import { getData, postData, putData, deleteData } from "../../../webService/webservice.js";
import "../../../styles/Auth/meta-config.css";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import {
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    Modal,
    Box,
    Typography,
    Button,
    CircularProgress,
    Tooltip
} from "@mui/material";

const MetaConfigPage = () => {
    const role = sessionStorage.getItem("role") || localStorage.getItem("role") || "";
    const sessionToken = sessionStorage.getItem("Token") || localStorage.getItem("Token") || "";
    const { selectedStoreId: contextStoreId } = useStore();

    const normRole = (role || "").toLowerCase().trim();
    const isSuperAdmin = normRole === "superadmin" || normRole === "selfadmin";

    // Determine assigned store IDs and current active store ID
    const activeHeaderStoreId = localStorage.getItem("currentStoreId") || sessionStorage.getItem("currentStoreId") || sessionStorage.getItem("storeId") || localStorage.getItem("storeId") || contextStoreId || "";
    const rawStoreIds = sessionStorage.getItem("storeIds") || localStorage.getItem("storeIds");
    const adminStoreIds = JSON.parse(rawStoreIds || "[]");
    const assignedStoreIds = adminStoreIds.length > 0 ? adminStoreIds : (activeHeaderStoreId ? [activeHeaderStoreId] : []);
    const allowedStoreIds = assignedStoreIds.map(id => (typeof id === 'object' ? String(id._id || id.id) : String(id))).filter(Boolean);

    // Initial selected store ID (prefer current store ID from header context)
    const initialSelectedStore = (activeHeaderStoreId && (isSuperAdmin || allowedStoreIds.length === 0 || allowedStoreIds.includes(activeHeaderStoreId)))
        ? activeHeaderStoreId
        : (allowedStoreIds[0] || activeHeaderStoreId || "");

    const [selectedStoreId, setSelectedStoreId] = useState(initialSelectedStore);
    const [stores, setStores] = useState([]);
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [testLoading, setTestLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("credentials");
    const [allConfigs, setAllConfigs] = useState([]);
    const [isEditing, setIsEditing] = useState(false);

    // Form fields
    const [formState, setFormState] = useState({
        businessName: "",
        displayName: "",
        appId: "",
        appSecret: "",
        businessManagerId: "",
        whatsappBusinessAccountId: "",
        phoneNumberId: "",
        businessPhoneNumber: "",
        permanentAccessToken: "",
        webhookVerifyToken: "",
        graphApiVersion: "v19.0",
        isWhatsAppBillingEnabled: true,
        defaultBillingTemplateName: "",
        defaultBillingTemplateLanguage: "en_US",
        variableMapping: {
            "store_name": "storeName",
            "1": "customerName",
            "2": "billNo",
            "3": "totalAmount",
            "4": "storeName"
        },
        varMap1: "customerName",
        varMap2: "billNo",
        varMap3: "totalAmount",
        varMap4: "storeName",
        varMap5: "invoiceDate"
    });

    const [storeTemplates, setStoreTemplates] = useState([]);
    const [detectedPlaceholders, setDetectedPlaceholders] = useState(["store_name"]);

    // Password visibility toggles
    const [showFields, setShowFields] = useState({
        appSecret: false,
        permanentAccessToken: false,
        webhookVerifyToken: false
    });

    // Modals
    const [refreshModalOpen, setRefreshModalOpen] = useState(false);
    const [newTokenValue, setNewTokenValue] = useState("");
    const [auditModalOpen, setAuditModalOpen] = useState(false);

    // Fetch store-specific WhatsApp templates for dynamic variable detection
    useEffect(() => {
        const fetchStoreTemplates = async () => {
            if (!selectedStoreId) return;
            try {
                const res = await getData(`store-whatsapp-templates/${selectedStoreId}`, {}, sessionToken);
                if (res?.status === 200 && res.data) {
                    const rawT = res.data.templates || res.data.results || (Array.isArray(res.data) ? res.data : []);
                    setStoreTemplates(Array.isArray(rawT) ? rawT : []);
                }
            } catch (err) {
                console.error("Error fetching store templates:", err);
            }
        };
        fetchStoreTemplates();
    }, [selectedStoreId, sessionToken]);

    // Detect actual placeholders from selected template
    useEffect(() => {
        const templateName = (formState.defaultBillingTemplateName || "purchase_receipt_3").trim();
        const matchedTemplate = storeTemplates.find(t =>
            (t.templateName || "").toLowerCase() === templateName.toLowerCase()
        );

        let rawPlaceholders = matchedTemplate?.placeholders || matchedTemplate?.variables || [];
        let cleanPlaceholders = rawPlaceholders.map(ph => String(ph).replace(/[{}]/g, '').trim()).filter(Boolean);

        if (cleanPlaceholders.length === 0 && matchedTemplate?.bodyText) {
            const matches = matchedTemplate.bodyText.match(/\{\{([^}]+)\}\}/g);
            if (matches) {
                matches.forEach(m => {
                    const clean = m.replace(/[{}]/g, '').trim();
                    if (clean && !cleanPlaceholders.includes(clean)) cleanPlaceholders.push(clean);
                });
            }
        }

        if (cleanPlaceholders.length === 0) {
            if (templateName.toLowerCase().includes("purchase_receipt")) {
                cleanPlaceholders = ["store_name"];
            } else {
                cleanPlaceholders = ["1", "2", "3", "4"];
            }
        }

        setDetectedPlaceholders(cleanPlaceholders);
    }, [formState.defaultBillingTemplateName, storeTemplates]);

    const handleVariableMapChange = (ph, value) => {
        setFormState(prev => ({
            ...prev,
            variableMapping: {
                ...(prev.variableMapping || {}),
                [ph]: value
            }
        }));
    };

    // Sync selectedStoreId when header store changes
    useEffect(() => {
        const storedStoreId = localStorage.getItem("currentStoreId") || sessionStorage.getItem("currentStoreId") || contextStoreId;
        if (storedStoreId && storedStoreId !== selectedStoreId) {
            if (isSuperAdmin || allowedStoreIds.length === 0 || allowedStoreIds.includes(storedStoreId)) {
                setSelectedStoreId(storedStoreId);
            }
        }
    }, [contextStoreId]);

    // ─────────────────────────────────────────────────────────────────────────
    // Fetch Store List (Role-based filtering)
    // ─────────────────────────────────────────────────────────────────────────
    useEffect(() => {
        const fetchStores = async () => {
            try {
                const res = await getData("stores/stores", { limit: 1000 }, sessionToken);
                if (res?.status === 200 || res?.success) {
                    const rawStores = res.data?.stores || res.data?.results || (Array.isArray(res.data) ? res.data : []);
                    const fetchedStores = Array.isArray(rawStores) ? rawStores : [];
                    
                    if (isSuperAdmin) {
                        setStores(fetchedStores);
                        if (!selectedStoreId && fetchedStores.length > 0) {
                            const curId = localStorage.getItem("currentStoreId") || fetchedStores[0]._id;
                            setSelectedStoreId(curId);
                        }
                    } else {
                        // RBAC: Filter stores to allowed assigned stores
                        let userStores = fetchedStores.filter(store => 
                            allowedStoreIds.some(aid => String(aid) === String(store._id))
                        );

                        // Fallback to session stores if API filter returned empty
                        if (userStores.length === 0) {
                            const rawStoresObj = sessionStorage.getItem("stores") || localStorage.getItem("stores");
                            const storageStores = JSON.parse(rawStoresObj || "[]");
                            if (Array.isArray(storageStores) && storageStores.length > 0) {
                                userStores = storageStores;
                            }
                        }

                        setStores(userStores);

                        // Prefer active header store ID if valid, otherwise fallback
                        const curHeaderId = localStorage.getItem("currentStoreId") || sessionStorage.getItem("currentStoreId") || contextStoreId;
                        const isValidCurHeader = userStores.some(s => String(s._id) === String(curHeaderId));
                        
                        if (isValidCurHeader) {
                            setSelectedStoreId(curHeaderId);
                        } else if (userStores.length > 0) {
                            setSelectedStoreId(userStores[0]._id);
                        } else if (allowedStoreIds.length > 0) {
                            setSelectedStoreId(allowedStoreIds[0]);
                        }
                    }
                }
            } catch (err) {
                console.error("Error fetching stores:", err);
            }
        };
        fetchStores();
    }, [sessionToken, isSuperAdmin, contextStoreId]);

    // ─────────────────────────────────────────────────────────────────────────
    // Fetch Meta Config for Selected Store
    // ─────────────────────────────────────────────────────────────────────────
    const fetchConfig = useCallback(async (storeIdToFetch) => {
        if (!storeIdToFetch) return;
        setLoading(true);
        try {
            const res = await getData(`meta-configs/${storeIdToFetch}`, {}, sessionToken);
            if (res?.status === 200 && res.data) {
                const cfg = res.data;
                setConfig(cfg);
                setIsEditing(true);
                setFormState({
                    businessName: cfg.businessName || "",
                    displayName: cfg.displayName || "",
                    appId: cfg.appId || "",
                    appSecret: "", // Never exposed by backend
                    businessManagerId: cfg.businessManagerId || "",
                    whatsappBusinessAccountId: cfg.whatsappBusinessAccountId || cfg.wabaId || "",
                    phoneNumberId: cfg.phoneNumberId || "",
                    businessPhoneNumber: cfg.businessPhoneNumber || cfg.displayPhoneNumber || "",
                    permanentAccessToken: "", // Never exposed by backend
                    webhookVerifyToken: "", // Never exposed by backend
                    graphApiVersion: cfg.graphApiVersion || "v19.0",
                    isWhatsAppBillingEnabled: cfg.isWhatsAppBillingEnabled !== false,
                    defaultBillingTemplateName: cfg.defaultBillingTemplateName || "",
                    defaultBillingTemplateLanguage: cfg.defaultBillingTemplateLanguage || "en_US",
                    varMap1: cfg.variableMapping?.["1"] || "customerName",
                    varMap2: cfg.variableMapping?.["2"] || "billNo",
                    varMap3: cfg.variableMapping?.["3"] || "totalAmount",
                    varMap4: cfg.variableMapping?.["4"] || "storeName",
                    varMap5: cfg.variableMapping?.["5"] || "invoiceDate"
                });
            } else {
                setConfig(null);
                setIsEditing(false);
                setFormState({
                    businessName: "",
                    displayName: "",
                    appId: "",
                    appSecret: "",
                    businessManagerId: "",
                    whatsappBusinessAccountId: "",
                    phoneNumberId: "",
                    businessPhoneNumber: "",
                    permanentAccessToken: "",
                    webhookVerifyToken: "",
                    graphApiVersion: "v19.0",
                    isWhatsAppBillingEnabled: true,
                    defaultBillingTemplateName: "",
                    defaultBillingTemplateLanguage: "en_US",
                    varMap1: "customerName",
                    varMap2: "billNo",
                    varMap3: "totalAmount",
                    varMap4: "storeName",
                    varMap5: "invoiceDate"
                });
            }
        } catch (err) {
            console.error("Error fetching meta config:", err);
            setConfig(null);
            setIsEditing(false);
        } finally {
            setLoading(false);
        }
    }, [sessionToken]);

    useEffect(() => {
        if (selectedStoreId) {
            fetchConfig(selectedStoreId);
        }
    }, [selectedStoreId, fetchConfig]);

    // ─────────────────────────────────────────────────────────────────────────
    // Fetch All Configs (for SuperAdmin Overview Tab)
    // ─────────────────────────────────────────────────────────────────────────
    const fetchAllConfigs = useCallback(async () => {
        if (!isSuperAdmin) return;
        try {
            const res = await getData("meta-configs", { limit: "all" }, sessionToken);
            if (res?.status === 200 && res.data) {
                const rawConfigs = res.data.configs || (Array.isArray(res.data) ? res.data : []);
                setAllConfigs(Array.isArray(rawConfigs) ? rawConfigs : []);
            }
        } catch (err) {
            console.error("Error fetching all meta configs:", err);
        }
    }, [isSuperAdmin, sessionToken]);

    useEffect(() => {
        if (activeTab === "all-stores" && isSuperAdmin) {
            fetchAllConfigs();
        }
    }, [activeTab, isSuperAdmin, fetchAllConfigs]);

    // Input Change Handler
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormState(prev => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value
        }));
    };

    // Toggle Field Password Visibility
    const toggleFieldVisibility = (field) => {
        setShowFields(prev => ({ ...prev, [field]: !prev[field] }));
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Save Meta Configuration (Create / Update)
    // ─────────────────────────────────────────────────────────────────────────
    const handleSave = async (e) => {
        e.preventDefault();

        if (!selectedStoreId) {
            toast.error("Please select a store first.");
            return;
        }

        if (!formState.appId.trim()) {
            toast.error("App ID (META_APP_ID) is required");
            return;
        }
        if (!formState.whatsappBusinessAccountId.trim()) {
            toast.error("WABA ID (META_WA_WABA_ID) is required");
            return;
        }
        if (!formState.phoneNumberId.trim()) {
            toast.error("Phone Number ID (META_WA_PHONE_NUMBER_ID) is required");
            return;
        }
        if (!isEditing && !formState.permanentAccessToken.trim()) {
            toast.error("Permanent Access Token (META_WA_ACCESS_TOKEN) is required");
            return;
        }

        setSaveLoading(true);

        const currentStoreObj = Array.isArray(stores) ? stores.find(s => s._id === selectedStoreId) : null;
        const storeNameFallback = currentStoreObj?.storeName || currentStoreObj?.branchName || "WhatsApp Business";

        const variableMapping = {
            "1": formState.varMap1 || "customerName",
            "2": formState.varMap2 || "billNo",
            "3": formState.varMap3 || "totalAmount",
            "4": formState.varMap4 || "storeName",
            "5": formState.varMap5 || "invoiceDate"
        };

        const payload = {
            storeId: selectedStoreId,
            businessName: formState.businessName.trim() || storeNameFallback,
            displayName: formState.displayName.trim() || undefined,
            appId: formState.appId.trim(),
            businessManagerId: formState.businessManagerId.trim() || formState.whatsappBusinessAccountId.trim(),
            whatsappBusinessAccountId: formState.whatsappBusinessAccountId.trim(),
            wabaId: formState.whatsappBusinessAccountId.trim(),
            phoneNumberId: formState.phoneNumberId.trim(),
            businessPhoneNumber: formState.businessPhoneNumber.trim() || formState.phoneNumberId.trim(),
            displayPhoneNumber: formState.businessPhoneNumber.trim() || formState.phoneNumberId.trim(),
            graphApiVersion: formState.graphApiVersion,
            isWhatsAppBillingEnabled: Boolean(formState.isWhatsAppBillingEnabled),
            defaultBillingTemplateName: formState.defaultBillingTemplateName.trim() || undefined,
            defaultBillingTemplateLanguage: formState.defaultBillingTemplateLanguage.trim() || "en_US",
            variableMapping
        };

        // Pass sensitive credentials or defaults
        if (formState.appSecret.trim()) {
            payload.appSecret = formState.appSecret.trim();
        } else if (!isEditing) {
            payload.appSecret = "default_app_secret";
        }

        if (formState.permanentAccessToken.trim()) {
            payload.permanentAccessToken = formState.permanentAccessToken.trim();
        }

        if (formState.webhookVerifyToken.trim()) {
            payload.webhookVerifyToken = formState.webhookVerifyToken.trim();
        } else if (!isEditing) {
            payload.webhookVerifyToken = "default_verify_token";
        }

        try {
            let res;
            if (isEditing) {
                res = await putData(`meta-configs/${selectedStoreId}`, payload, sessionToken);
            } else {
                res = await postData("meta-configs", payload, sessionToken);
            }

            if (res?.status === 200 || res?.success || res?.status === 201) {
                toast.success(isEditing ? "Meta configuration updated successfully!" : "Meta configuration created successfully!");
                fetchConfig(selectedStoreId);
            } else {
                toast.error(res?.message || "Failed to save Meta configuration.");
            }
        } catch (err) {
            console.error("Save error:", err);
            toast.error(err.message || "Server error while saving configuration.");
        } finally {
            setSaveLoading(false);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Test Meta API Connection
    // ─────────────────────────────────────────────────────────────────────────
    const handleTestConnection = async () => {
        if (!selectedStoreId || !config) {
            toast.warning("Please save configuration first before testing.");
            return;
        }

        setTestLoading(true);
        try {
            const res = await postData(`meta-configs/${selectedStoreId}/test`, {}, sessionToken);
            if (res?.status === 200 || res?.success) {
                Swal.fire({
                    icon: "success",
                    title: "Connection Successful!",
                    html: `
                        <div style="text-align:left; font-size:14px; line-height:1.6;">
                            <p><strong>Status:</strong> <span style="color:#16a34a;">Connected</span></p>
                            <p><strong>Verified Phone:</strong> ${res.data?.metaPhoneData?.display_phone_number || config.businessPhoneNumber || 'N/A'}</p>
                            <p><strong>Verified Name:</strong> ${res.data?.metaPhoneData?.verified_name || 'N/A'}</p>
                            <p><strong>Quality Rating:</strong> ${res.data?.metaPhoneData?.quality_rating || 'GREEN'}</p>
                        </div>
                    `,
                    confirmButtonColor: "#2563eb"
                });
                fetchConfig(selectedStoreId);
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Connection Failed",
                    text: res?.message || "Could not connect to Meta Graph API using the stored credentials.",
                    confirmButtonColor: "#ef4444"
                });
                fetchConfig(selectedStoreId);
            }
        } catch (err) {
            console.error("Test connection error:", err);
            toast.error(err.message || "Failed to test connection.");
        } finally {
            setTestLoading(false);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Mark as Connected
    // ─────────────────────────────────────────────────────────────────────────
    const handleConnect = async () => {
        if (!selectedStoreId) return;
        try {
            const res = await postData(`meta-configs/${selectedStoreId}/connect`, {}, sessionToken);
            if (res?.status === 200 || res?.success) {
                toast.success("Meta account marked as Connected!");
                fetchConfig(selectedStoreId);
            } else {
                toast.error(res?.message || "Failed to connect.");
            }
        } catch (err) {
            toast.error(err.message || "Error connecting.");
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Mark as Disconnected
    // ─────────────────────────────────────────────────────────────────────────
    const handleDisconnect = async () => {
        if (!selectedStoreId) return;
        const confirm = await Swal.fire({
            title: "Disconnect Meta Account?",
            text: "This will disconnect WhatsApp integration for this store. Outgoing campaign messages will be paused.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#ea580c",
            cancelButtonColor: "#64748b",
            confirmButtonText: "Yes, Disconnect"
        });

        if (confirm.isConfirmed) {
            try {
                const res = await postData(`meta-configs/${selectedStoreId}/disconnect`, {}, sessionToken);
                if (res?.status === 200 || res?.success) {
                    toast.warning("Meta account disconnected.");
                    fetchConfig(selectedStoreId);
                } else {
                    toast.error(res?.message || "Failed to disconnect.");
                }
            } catch (err) {
                toast.error(err.message || "Error disconnecting.");
            }
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Delete Configuration
    // ─────────────────────────────────────────────────────────────────────────
    const handleDelete = async () => {
        if (!selectedStoreId) return;
        const confirm = await Swal.fire({
            title: "Delete Meta Configuration?",
            text: "Are you sure you want to delete this configuration? This action will remove stored credentials for this store.",
            icon: "error",
            showCancelButton: true,
            confirmButtonColor: "#ef4444",
            cancelButtonColor: "#64748b",
            confirmButtonText: "Yes, Delete"
        });

        if (confirm.isConfirmed) {
            try {
                const res = await deleteData(`meta-configs/${selectedStoreId}`, sessionToken);
                if (res?.status === 200 || res?.success) {
                    toast.success("Meta configuration deleted.");
                    fetchConfig(selectedStoreId);
                } else {
                    toast.error(res?.message || "Failed to delete configuration.");
                }
            } catch (err) {
                toast.error(err.message || "Error deleting configuration.");
            }
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Submit Refresh Access Token
    // ─────────────────────────────────────────────────────────────────────────
    const handleRefreshTokenSubmit = async () => {
        if (!newTokenValue.trim()) {
            toast.warning("Please enter a new access token.");
            return;
        }

        try {
            const res = await postData(`meta-configs/${selectedStoreId}/refresh-token`, {
                newAccessToken: newTokenValue.trim()
            }, sessionToken);

            if (res?.status === 200 || res?.success) {
                toast.success("Access token refreshed and re-encrypted successfully!");
                setRefreshModalOpen(false);
                setNewTokenValue("");
                fetchConfig(selectedStoreId);
            } else {
                toast.error(res?.message || "Failed to refresh access token.");
            }
        } catch (err) {
            toast.error(err.message || "Error refreshing access token.");
        }
    };

    // Copy Webhook URL to Clipboard
    const copyWebhookUrl = () => {
        const url = config?.webhookCallbackUrl || `${window.location.origin}/api/v1/meta-configs/${selectedStoreId}/webhook`;
        navigator.clipboard.writeText(url);
        toast.success("Webhook URL copied to clipboard!");
    };

    // Render Status Chip
    const renderStatusChip = (status) => {
        const statusLower = (status || "pending").toLowerCase();
        return (
            <div className={`status-badge-chip ${statusLower}`}>
                <span className="pulse-dot"></span>
                {status || "Pending"}
            </div>
        );
    };

    return (
        <div className="product-layout">


            <div className="meta-config-container">

                {/* Header Bar */}
                <div className="meta-config-header">
                    <div className="meta-config-title-area">
                        <h2>
                            <Icon icon="mdi:whatsapp" width="28" height="28" style={{ color: "#25D366" }} />
                            Meta (WhatsApp Business) Configuration
                        </h2>
                        <p>Manage WhatsApp Business API App credentials, tokens, and webhooks per store</p>
                    </div>
                    <div className="meta-config-header-actions">
                        {config && renderStatusChip(config.connectionStatus)}
                        {config && (
                            <button className="btn-action-secondary" onClick={() => setAuditModalOpen(true)}>
                                <Icon icon="mdi:clipboard-text-clock-outline" width="18" height="18" />
                                Audit Log
                            </button>
                        )}
                        <button className="btn-action-primary" onClick={handleSave} disabled={saveLoading || loading}>
                            {saveLoading ? <CircularProgress size={16} color="inherit" /> : <Icon icon="mdi:content-save-outline" width="18" height="18" />}
                            {isEditing ? "Save Changes" : "Create Configuration"}
                        </button>
                    </div>
                </div>

                {/* Store Selector Bar: Show dropdown only if SuperAdmin (has stores) OR if StoreAdmin manages multiple assigned stores (>1) */}
                {((isSuperAdmin && Array.isArray(stores) && stores.length > 0) || (!isSuperAdmin && Array.isArray(stores) && stores.length > 1)) && (
                    <div className="superadmin-store-bar">
                        <div className="superadmin-store-label">
                            <Icon icon="mdi:store-cog-outline" width="20" height="20" style={{ color: "#2563eb" }} />
                            Select Store:
                        </div>
                        <select
                            className="store-select-dropdown"
                            value={selectedStoreId}
                            onChange={(e) => setSelectedStoreId(e.target.value)}
                        >
                            {stores.map(store => (
                                <option key={store._id} value={store._id}>
                                    {store.storeName || store.branchName} ({store.branchName || store.location || 'Store'})
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {/* Top Stat Cards Row */}
                <div className="meta-stats-row">
                    <div className="meta-stat-card">
                        <div className="meta-stat-icon status">
                            <Icon icon="mdi:link-variant" />
                        </div>
                        <div className="meta-stat-info">
                            <div className="stat-value">{config?.connectionStatus || "Not Configured"}</div>
                            <div className="stat-label">Connection Status</div>
                        </div>
                    </div>

                    <div className="meta-stat-card">
                        <div className="meta-stat-icon sync">
                            <Icon icon="mdi:sync" />
                        </div>
                        <div className="meta-stat-info">
                            <div className="stat-value" style={{ fontSize: '13px' }}>
                                {config?.lastSyncAt ? new Date(config.lastSyncAt).toLocaleString('en-IN') : "Never"}
                            </div>
                            <div className="stat-label">Last Synchronization</div>
                        </div>
                    </div>

                    <div className="meta-stat-card">
                        <div className="meta-stat-icon phone">
                            <Icon icon="mdi:phone-outgoing-outline" />
                        </div>
                        <div className="meta-stat-info">
                            <div className="stat-value" style={{ fontSize: '13px' }}>
                                {config?.businessPhoneNumber || "Not Set"}
                            </div>
                            <div className="stat-label">Business Phone Number</div>
                        </div>
                    </div>

                    <div className="meta-stat-card">
                        <div className="meta-stat-icon api">
                            <Icon icon="mdi:api" />
                        </div>
                        <div className="meta-stat-info">
                            <div className="stat-value">{config?.graphApiVersion || "v19.0"}</div>
                            <div className="stat-label">Graph API Version</div>
                        </div>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="meta-config-tabs">
                    <button
                        className={`meta-tab-button ${activeTab === "credentials" ? "active" : ""}`}
                        onClick={() => setActiveTab("credentials")}
                    >
                        <Icon icon="mdi:key-outline" width="18" height="18" />
                        Credentials & Setup
                    </button>

                    <button
                        className={`meta-tab-button ${activeTab === "webhook" ? "active" : ""}`}
                        onClick={() => setActiveTab("webhook")}
                    >
                        <Icon icon="mdi:webhook" width="18" height="18" />
                        Webhook Configuration
                    </button>

                    <button
                        className={`meta-tab-button ${activeTab === "actions" ? "active" : ""}`}
                        onClick={() => setActiveTab("actions")}
                    >
                        <Icon icon="mdi:lightning-bolt-outline" width="18" height="18" />
                        Actions & Control
                    </button>

                    {isSuperAdmin && (
                        <button
                            className={`meta-tab-button ${activeTab === "all-stores" ? "active" : ""}`}
                            onClick={() => setActiveTab("all-stores")}
                        >
                            <Icon icon="mdi:store-outline" width="18" height="18" />
                            All Stores Overview
                        </button>
                    )}
                </div>

                {/* Loading Indicator */}
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                        <CircularProgress />
                        <Typography ml={2} color="textSecondary">Loading Meta configuration...</Typography>
                    </Box>
                ) : (
                    <>
                        {/* TAB 1: Credentials Form */}
                        {activeTab === "credentials" && (
                            <div className="meta-config-card">
                                <div className="meta-config-card-header">
                                    <h3>
                                        <Icon icon="mdi:shield-lock-outline" width="20" height="20" style={{ color: "#2563eb" }} />
                                        Store Credentials Configuration
                                    </h3>
                                    {isEditing && (
                                        <span style={{ fontSize: '12px', color: '#64748b' }}>
                                            Editing existing configuration for store ID: <strong>{selectedStoreId}</strong>
                                        </span>
                                    )}
                                </div>

                                <div className="meta-config-card-body">
                                    <form onSubmit={handleSave}>

                                        {/* Section 1: Business Identity */}
                                        <div className="form-section-title">
                                            <Icon icon="mdi:domain" width="16" height="16" />
                                            1. Business Identity
                                        </div>

                                        <div className="form-grid-2">
                                            <div className="form-group-field">
                                                <label>
                                                    Business Name <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 400 }}>(Optional - Defaults to Store Name)</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    name="businessName"
                                                    className="custom-input"
                                                    placeholder="e.g. Acme Retail Pvt Ltd"
                                                    value={formState.businessName}
                                                    onChange={handleInputChange}
                                                />
                                            </div>

                                            <div className="form-group-field">
                                                <label>WhatsApp Display Name <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 400 }}>(Optional)</span></label>
                                                <input
                                                    type="text"
                                                    name="displayName"
                                                    className="custom-input"
                                                    placeholder="e.g. Acme Support"
                                                    value={formState.displayName}
                                                    onChange={handleInputChange}
                                                />
                                                <span className="field-hint-text">Name shown on your WhatsApp Business Profile</span>
                                            </div>
                                        </div>

                                        {/* Section 2: Meta App Credentials */}
                                        <div className="form-section-title">
                                            <Icon icon="mdi:application-cog-outline" width="16" height="16" />
                                            2. Meta App & Business Credentials
                                        </div>

                                        <div className="form-grid-2">
                                            <div className="form-group-field">
                                                <label>
                                                    App ID (META_APP_ID) <span className="required-star">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    name="appId"
                                                    className="custom-input"
                                                    placeholder="e.g. 123456789012345"
                                                    value={formState.appId}
                                                    onChange={handleInputChange}
                                                    required
                                                />
                                            </div>

                                            <div className="form-group-field">
                                                <label>
                                                    App Secret <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 400 }}>(Optional)</span>
                                                    <span className="encrypted-badge">🔒 AES-256 Encrypted</span>
                                                </label>
                                                <div className="input-with-action">
                                                    <input
                                                        type={showFields.appSecret ? "text" : "password"}
                                                        name="appSecret"
                                                        className="custom-input"
                                                        placeholder={isEditing ? "🔒 Encrypted at rest — enter new value to update" : "Paste Meta App Secret (Optional)"}
                                                        value={formState.appSecret}
                                                        onChange={handleInputChange}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="toggle-eye-btn"
                                                        onClick={() => toggleFieldVisibility("appSecret")}
                                                    >
                                                        <Icon icon={showFields.appSecret ? "mdi:eye-off" : "mdi:eye"} width="18" height="18" />
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="form-group-field">
                                                <label>
                                                    Business Manager ID <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 400 }}>(Optional - Defaults to WABA ID)</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    name="businessManagerId"
                                                    className="custom-input"
                                                    placeholder="e.g. 987654321098765"
                                                    value={formState.businessManagerId}
                                                    onChange={handleInputChange}
                                                />
                                            </div>

                                            <div className="form-group-field">
                                                <label>
                                                    WhatsApp Business Account ID (META_WA_WABA_ID) <span className="required-star">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    name="whatsappBusinessAccountId"
                                                    className="custom-input"
                                                    placeholder="e.g. 112233445566778"
                                                    value={formState.whatsappBusinessAccountId}
                                                    onChange={handleInputChange}
                                                    required
                                                />
                                            </div>
                                        </div>

                                        {/* Section 3: Phone Settings & Graph API */}
                                        <div className="form-section-title">
                                            <Icon icon="mdi:cellphone-cog" width="16" height="16" />
                                            3. Phone Number & Graph API Settings
                                        </div>

                                        <div className="form-grid-2">
                                            <div className="form-group-field">
                                                <label>
                                                    Phone Number ID (META_WA_PHONE_NUMBER_ID) <span className="required-star">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    name="phoneNumberId"
                                                    className="custom-input"
                                                    placeholder="e.g. 198765432109876"
                                                    value={formState.phoneNumberId}
                                                    onChange={handleInputChange}
                                                    required
                                                />
                                            </div>

                                            <div className="form-group-field">
                                                <label>
                                                    Business Phone Number <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 400 }}>(Optional)</span>
                                                </label>
                                                <input
                                                    type="tel"
                                                    name="businessPhoneNumber"
                                                    className="custom-input"
                                                    placeholder="e.g. +919876543210"
                                                    value={formState.businessPhoneNumber}
                                                    onChange={handleInputChange}
                                                />
                                                <span className="field-hint-text">Include country code with + sign (E.164 format)</span>
                                            </div>

                                            <div className="form-group-field">
                                                <label>Graph API Version</label>
                                                <select
                                                    name="graphApiVersion"
                                                    className="custom-input"
                                                    value={formState.graphApiVersion}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value="v19.0">v19.0 (Recommended)</option>
                                                    <option value="v20.0">v20.0</option>
                                                    <option value="v21.0">v21.0</option>
                                                    <option value="v22.0">v22.0 (Latest)</option>
                                                    <option value="v18.0">v18.0 (Legacy)</option>
                                                </select>
                                            </div>
                                        </div>

                                        {/* Section 4: Security Tokens */}
                                        <div className="form-section-title">
                                            <Icon icon="mdi:lock-reset" width="16" height="16" />
                                            4. Permanent Access Token & Webhook Token
                                        </div>

                                        <div className="form-grid-1">
                                            <div className="form-group-field">
                                                <label>
                                                    Permanent Access Token (META_WA_ACCESS_TOKEN) <span className="required-star">*</span>
                                                    <span className="encrypted-badge">🔒 AES-256 Encrypted</span>
                                                </label>
                                                <div className="input-with-action">
                                                    <textarea
                                                        name="permanentAccessToken"
                                                        className="custom-input custom-textarea"
                                                        placeholder={isEditing ? "🔒 Encrypted at rest — enter new value to update (or use Refresh Token action)" : "Paste long-lived System User Access Token"}
                                                        value={formState.permanentAccessToken}
                                                        onChange={handleInputChange}
                                                    />
                                                </div>
                                                <span className="field-hint-text">Generate a permanent token from Meta Business Manager → System Users</span>
                                            </div>

                                            <div className="form-group-field">
                                                <label>
                                                    Webhook Verify Token <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 400 }}>(Optional)</span>
                                                    <span className="encrypted-badge">🔒 AES-256 Encrypted</span>
                                                </label>
                                                <div className="input-with-action">
                                                    <input
                                                        type={showFields.webhookVerifyToken ? "text" : "password"}
                                                        name="webhookVerifyToken"
                                                        className="custom-input"
                                                        placeholder={isEditing ? "🔒 Encrypted at rest — enter new value to update" : "Set custom secret string for Meta webhook verification (Optional)"}
                                                        value={formState.webhookVerifyToken}
                                                        onChange={handleInputChange}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="toggle-eye-btn"
                                                        onClick={() => toggleFieldVisibility("webhookVerifyToken")}
                                                    >
                                                        <Icon icon={showFields.webhookVerifyToken ? "mdi:eye-off" : "mdi:eye"} width="18" height="18" />
                                                    </button>
                                                </div>
                                                <span className="field-hint-text">Secret string passed by Meta when subscribing to webhook events</span>
                                            </div>
                                        </div>

                                        {/* Section 5: WhatsApp Billing & Store Billing Template Configuration */}
                                        <div className="form-section-title">
                                            <Icon icon="mdi:receipt-text-outline" width="16" height="16" />
                                            5. WhatsApp Billing & Store Billing Template Configuration
                                        </div>

                                        <div className="form-grid-2">
                                            <div className="form-group-field" style={{ gridColumn: 'span 2' }}>
                                                <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 600 }}>
                                                    <input
                                                        type="checkbox"
                                                        name="isWhatsAppBillingEnabled"
                                                        checked={formState.isWhatsAppBillingEnabled}
                                                        onChange={handleInputChange}
                                                        style={{ width: '18px', height: '18px', accentColor: '#16a34a' }}
                                                    />
                                                    Enable WhatsApp Billing for this Store
                                                </label>
                                                <span className="field-hint-text">Allows sending billing invoices via WhatsApp for this store</span>
                                            </div>

                                            <div className="form-group-field">
                                                <label>
                                                    Approved WhatsApp Billing Template Name
                                                </label>
                                                <input
                                                    type="text"
                                                    name="defaultBillingTemplateName"
                                                    className="custom-input"
                                                    placeholder="e.g. purchase_receipt_3 or store_a_invoice"
                                                    value={formState.defaultBillingTemplateName}
                                                    onChange={handleInputChange}
                                                />
                                                <span className="field-hint-text">Leave blank to use default global billing template (purchase_receipt_3)</span>
                                            </div>

                                            <div className="form-group-field">
                                                <label>
                                                    Template Language Code
                                                </label>
                                                <input
                                                    type="text"
                                                    name="defaultBillingTemplateLanguage"
                                                    className="custom-input"
                                                    placeholder="e.g. en_US or en"
                                                    value={formState.defaultBillingTemplateLanguage}
                                                    onChange={handleInputChange}
                                                />
                                                <span className="field-hint-text">Language code of approved Meta template</span>
                                            </div>
                                        </div>

                                        {/* Dynamic Template Parameter Mapping */}
                                        <div className="form-section-title" style={{ marginTop: '16px', fontSize: '13px' }}>
                                            <Icon icon="mdi:variable" width="14" height="14" />
                                            Template Parameter Mapping (Detected Meta Variables: {detectedPlaceholders.map(p => `{{${p}}}`).join(", ")})
                                        </div>

                                        <div className="form-grid-2">
                                            {detectedPlaceholders.map((ph, idx) => {
                                                const currentMap = formState.variableMapping?.[ph] || formState.variableMapping?.[String(idx + 1)] ||
                                                    (ph.toLowerCase().includes("store") ? "storeName" :
                                                     ph.toLowerCase().includes("customer") ? "customerName" :
                                                     ph.toLowerCase().includes("bill") || ph.toLowerCase().includes("invoice") ? "billNo" :
                                                     ph.toLowerCase().includes("amount") ? "totalAmount" :
                                                     ph === "1" ? "customerName" :
                                                     ph === "2" ? "billNo" :
                                                     ph === "3" ? "totalAmount" : "storeName");

                                                return (
                                                    <div className="form-group-field" key={ph}>
                                                        <label>{"{{"}{ph}{"}}"} Placeholder Variable</label>
                                                        <select
                                                            name={`varMap_${ph}`}
                                                            className="custom-input"
                                                            value={currentMap}
                                                            onChange={(e) => handleVariableMapChange(ph, e.target.value)}
                                                        >
                                                            <option value="storeName">Store Name</option>
                                                            <option value="customerName">Customer Name</option>
                                                            <option value="billNo">Invoice / Bill Number</option>
                                                            <option value="totalAmount">Total Bill Amount</option>
                                                            <option value="invoiceDate">Invoice Date</option>
                                                            <option value="none">None / Not Used</option>
                                                        </select>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="meta-action-buttons-wrapper">
                                            <button
                                                type="submit"
                                                className="btn-action-primary"
                                                disabled={saveLoading}
                                            >
                                                {saveLoading ? <CircularProgress size={16} color="inherit" /> : <Icon icon="mdi:content-save-outline" width="18" height="18" />}
                                                {isEditing ? "Save Changes" : "Create Meta Config"}
                                            </button>

                                            {isEditing && (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="btn-action-success"
                                                        onClick={handleTestConnection}
                                                        disabled={testLoading}
                                                    >
                                                        {testLoading ? <CircularProgress size={16} color="inherit" /> : <Icon icon="mdi:test-tube" width="18" height="18" />}
                                                        Test Connection
                                                    </button>

                                                    <button
                                                        type="button"
                                                        className="btn-action-warning"
                                                        onClick={() => setRefreshModalOpen(true)}
                                                    >
                                                        <Icon icon="mdi:refresh" width="18" height="18" />
                                                        Refresh Access Token
                                                    </button>
                                                </>
                                            )}
                                        </div>

                                    </form>
                                </div>
                            </div>
                        )}

                        {/* TAB 2: Webhook Configuration */}
                        {activeTab === "webhook" && (
                            <div className="meta-config-card">
                                <div className="meta-config-card-header">
                                    <h3>
                                        <Icon icon="mdi:webhook" width="20" height="20" style={{ color: "#16a34a" }} />
                                        Auto-Generated Webhook Endpoint
                                    </h3>
                                </div>
                                <div className="meta-config-card-body">
                                    <p style={{ fontSize: "14px", color: "#475569", marginBottom: "16px" }}>
                                        Configure this Webhook Callback URL in the Meta Developer Portal under <strong>WhatsApp → Configuration → Edit Webhook</strong>.
                                    </p>

                                    <div className="webhook-display-box">
                                        <div className="webhook-url-code">
                                            {config?.webhookCallbackUrl || `${window.location.origin}/api/v1/meta-configs/${selectedStoreId}/webhook`}
                                        </div>
                                        <button className="btn-action-secondary" onClick={copyWebhookUrl}>
                                            <Icon icon="mdi:content-copy" width="18" height="18" />
                                            Copy URL
                                        </button>
                                    </div>

                                    <div style={{ marginTop: '24px', background: '#f8fafc', padding: '20px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                                        <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', color: '#0f172a' }}>📋 Setup Instructions</h4>
                                        <ol style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#334155', lineHeight: '1.8' }}>
                                            <li>Go to <strong>developers.facebook.com</strong> and open your App Dashboard.</li>
                                            <li>Navigate to <strong>WhatsApp → Configuration</strong> in the left sidebar.</li>
                                            <li>Click <strong>Edit</strong> under Webhook.</li>
                                            <li>Paste the <strong>Callback URL</strong> copied above.</li>
                                            <li>Enter the <strong>Webhook Verify Token</strong> you configured in the Credentials tab.</li>
                                            <li>Click <strong>Verify and Save</strong>.</li>
                                            <li>Subscribe to the <strong>messages</strong> webhook field to receive incoming WhatsApp messages and status updates.</li>
                                        </ol>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* TAB 3: Actions & Control */}
                        {activeTab === "actions" && (
                            <div className="meta-config-card">
                                <div className="meta-config-card-header">
                                    <h3>
                                        <Icon icon="mdi:lightning-bolt-outline" width="20" height="20" style={{ color: "#ea580c" }} />
                                        Operational Controls & Management
                                    </h3>
                                </div>
                                <div className="meta-config-card-body">
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>

                                        {/* Test Connection Card */}
                                        <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                                            <h4 style={{ margin: '0 0 8px 0', fontSize: '15px', color: '#0f172a' }}>🧪 Test Connection</h4>
                                            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
                                                Call Meta Graph API to verify store phone number & access token.
                                            </p>
                                            <button className="btn-action-primary" onClick={handleTestConnection} disabled={testLoading || !isEditing}>
                                                {testLoading ? <CircularProgress size={16} color="inherit" /> : <Icon icon="mdi:test-tube" width="18" height="18" />}
                                                Run Test Connection
                                            </button>
                                        </div>

                                        {/* Manual Connect Card */}
                                        <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                                            <h4 style={{ margin: '0 0 8px 0', fontSize: '15px', color: '#0f172a' }}>✅ Mark as Connected</h4>
                                            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
                                                Set integration status to Connected to enable campaign dispatches.
                                            </p>
                                            <button className="btn-action-success" onClick={handleConnect} disabled={!isEditing}>
                                                <Icon icon="mdi:check-circle-outline" width="18" height="18" />
                                                Set Connected
                                            </button>
                                        </div>

                                        {/* Disconnect Card */}
                                        <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                                            <h4 style={{ margin: '0 0 8px 0', fontSize: '15px', color: '#0f172a' }}>⚠️ Disconnect Account</h4>
                                            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
                                                Pause integration. Outgoing WhatsApp dispatches will be stopped.
                                            </p>
                                            <button className="btn-action-warning" onClick={handleDisconnect} disabled={!isEditing}>
                                                <Icon icon="mdi:power-plug-off-outline" width="18" height="18" />
                                                Disconnect Integration
                                            </button>
                                        </div>

                                        {/* Delete Card */}
                                        <div style={{ background: '#fef2f2', padding: '20px', borderRadius: '10px', border: '1px solid #fca5a5' }}>
                                            <h4 style={{ margin: '0 0 8px 0', fontSize: '15px', color: '#991b1b' }}>🗑️ Delete Configuration</h4>
                                            <p style={{ fontSize: '13px', color: '#7f1d1d', marginBottom: '16px' }}>
                                                Permanently soft-delete credentials for this store.
                                            </p>
                                            <button className="btn-action-danger" onClick={handleDelete} disabled={!isEditing}>
                                                <Icon icon="mdi:trash-can-outline" width="18" height="18" />
                                                Delete Configuration
                                            </button>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        )}

                        {/* TAB 4: All Stores Overview (SuperAdmin) */}
                        {activeTab === "all-stores" && isSuperAdmin && (
                            <TableContainer component={Paper} elevation={1} style={{ borderRadius: '12px' }}>
                                <Table>
                                    <TableHead style={{ backgroundColor: '#f8fafc' }}>
                                        <TableRow>
                                            <TableCell style={{ fontWeight: 700 }}>Store Name</TableCell>
                                            <TableCell style={{ fontWeight: 700 }}>Business Name</TableCell>
                                            <TableCell style={{ fontWeight: 700 }}>Business Phone</TableCell>
                                            <TableCell style={{ fontWeight: 700 }}>API Version</TableCell>
                                            <TableCell style={{ fontWeight: 700 }}>Status</TableCell>
                                            <TableCell style={{ fontWeight: 700 }}>Last Sync</TableCell>
                                            <TableCell style={{ fontWeight: 700 }} align="center">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {allConfigs.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={7} align="center" style={{ padding: '30px' }}>
                                                    No Meta configurations found across stores.
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            allConfigs.map((cfg) => (
                                                <TableRow key={cfg._id} hover>
                                                    <TableCell style={{ fontWeight: 600 }}>
                                                        {cfg.storeId?.storeName || cfg.storeId?.branchName || "Store"}
                                                    </TableCell>
                                                    <TableCell>{cfg.businessName}</TableCell>
                                                    <TableCell>{cfg.businessPhoneNumber}</TableCell>
                                                    <TableCell>{cfg.graphApiVersion}</TableCell>
                                                    <TableCell>{renderStatusChip(cfg.connectionStatus)}</TableCell>
                                                    <TableCell style={{ fontSize: '12px' }}>
                                                        {cfg.lastSyncAt ? new Date(cfg.lastSyncAt).toLocaleString('en-IN') : "Never"}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => {
                                                                const sId = cfg.storeId?._id || cfg.storeId;
                                                                setSelectedStoreId(sId);
                                                                setActiveTab("credentials");
                                                            }}
                                                        >
                                                            Manage
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </>
                )}

                {/* Refresh Token Modal */}
                <Modal open={refreshModalOpen} onClose={() => setRefreshModalOpen(false)}>
                    <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 520,
                        bgcolor: 'background.paper',
                        borderRadius: 3,
                        boxShadow: 24,
                        p: 4,
                    }}>
                        <Typography variant="h6" component="h2" fontWeight={700} mb={1}>
                            🔄 Refresh Permanent Access Token
                        </Typography>
                        <Typography variant="body2" color="textSecondary" mb={3}>
                            Paste the new long-lived System User Access Token from Meta Developer Console. It will be AES-256 encrypted immediately.
                        </Typography>

                        <div className="form-group-field" style={{ marginBottom: '20px' }}>
                            <label>New Permanent Access Token <span className="required-star">*</span></label>
                            <textarea
                                className="custom-input custom-textarea"
                                placeholder="Paste new access token here..."
                                value={newTokenValue}
                                onChange={(e) => setNewTokenValue(e.target.value)}
                                rows={4}
                            />
                        </div>

                        <Box display="flex" justifyContent="flex-end" gap={1.5}>
                            <Button variant="outlined" onClick={() => setRefreshModalOpen(false)}>
                                Cancel
                            </Button>
                            <Button variant="contained" color="primary" onClick={handleRefreshTokenSubmit}>
                                Encrypt & Save Token
                            </Button>
                        </Box>
                    </Box>
                </Modal>

                {/* Audit Log Modal */}
                <Modal open={auditModalOpen} onClose={() => setAuditModalOpen(false)}>
                    <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 620,
                        maxHeight: '80vh',
                        bgcolor: 'background.paper',
                        borderRadius: 3,
                        boxShadow: 24,
                        p: 4,
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <Typography variant="h6" component="h2" fontWeight={700} mb={2}>
                            📋 Configuration Audit Trail
                        </Typography>

                        <div className="audit-timeline-container" style={{ flex: 1 }}>
                            {!config?.auditLogs || config.auditLogs.length === 0 ? (
                                <Typography color="textSecondary" py={3} textAlign="center">
                                    No audit log entries found.
                                </Typography>
                            ) : (
                                [...config.auditLogs].reverse().map((logItem, index) => (
                                    <div className="audit-timeline-item" key={logItem._id || index}>
                                        <div className={`audit-icon-circle ${logItem.status === 'SUCCESS' ? 'success' : 'failure'}`}>
                                            <Icon icon={logItem.status === 'SUCCESS' ? 'mdi:check' : 'mdi:alert-circle-outline'} />
                                        </div>
                                        <div className="audit-item-content">
                                            <div className="audit-item-title">
                                                <span>{logItem.action}</span>
                                                {renderStatusChip(logItem.status)}
                                            </div>
                                            <div className="audit-item-details">
                                                {logItem.details || 'No details recorded'}
                                            </div>
                                            <div className="audit-item-time">
                                                🕐 {new Date(logItem.timestamp).toLocaleString('en-IN')}
                                                {logItem.performedBy?.name && ` | By: ${logItem.performedBy.name}`}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        <Box display="flex" justifyContent="flex-end" mt={3}>
                            <Button variant="outlined" onClick={() => setAuditModalOpen(false)}>
                                Close
                            </Button>
                        </Box>
                    </Box>
                </Modal>

            </div>
        </div>
    );
};

export default MetaConfigPage;
